import { collection, addDoc, serverTimestamp, getFirestore, getDocs, query, where, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { getApps } from 'firebase/app';
import { db as firebaseDb } from '../firebase';

/**
 * Analytics service for tracking user interactions and page views
 */

// Helper function to get a valid db instance
const getDb = () => {
  // First try the imported db
  if (firebaseDb) {
    return firebaseDb;
  }
  
  // Fallback: try to get it from initialized apps
  try {
    const apps = getApps();
    if (apps.length > 0) {
      return getFirestore(apps[0]);
    }
  } catch (e) {
    console.error('Could not recover Firestore instance:', e);
  }
  
  console.error('❌ Firestore db is not initialized. Check firebase.js configuration.');
  return null;
};

// Get IP address and geolocation using a free API service
const getIPAddressAndLocation = async () => {
  try {
    // Try multiple free IP + geolocation services with CORS-friendly endpoints
    const services = [
      {
        url: 'https://api.ipify.org?format=json',
        parser: (data) => ({
          ip: data.ip,
          country: null,
          countryCode: null,
          region: null,
          city: null,
          lat: null,
          lon: null,
          timezone: null,
          isp: null
        })
      },
      {
        url: 'https://ipapi.co/json/',
        parser: (data) => ({
          ip: data.ip,
          country: data.country_name || null,
          countryCode: data.country_code || null,
          region: data.region || null,
          city: data.city || null,
          lat: data.latitude || null,
          lon: data.longitude || null,
          timezone: data.timezone || null,
          isp: data.org || null
        })
      },
      {
        url: 'https://ipgeolocation.abstractapi.com/v1/?api_key=free',
        parser: (data) => ({
          ip: data.ip_address || null,
          country: data.country || null,
          countryCode: data.country_code || null,
          region: data.region || null,
          city: data.city || null,
          lat: data.latitude ? parseFloat(data.latitude) : null,
          lon: data.longitude ? parseFloat(data.longitude) : null,
          timezone: data.timezone?.name || null,
          isp: data.connection?.isp_name || null
        })
      }
    ];
    
    for (const service of services) {
      try {
        const response = await fetch(service.url, { 
          method: 'GET',
          headers: { 
            'Accept': 'application/json',
            'User-Agent': 'Mozilla/5.0 (compatible; Portfolio-Analytics/1.0)'
          },
          mode: 'cors', // Explicitly set CORS mode
          cache: 'no-cache'
        });
        
        if (response.ok) {
          const data = await response.json();
          const locationData = service.parser(data);
          
          if (locationData.ip) {
            console.log('✅ Successfully fetched location data from:', service.url);
            return sanitizeLocationData(locationData);
          }
        } else {
          console.warn(`Service ${service.url} returned status:`, response.status);
        }
      } catch (e) {
        console.warn(`Failed to fetch from ${service.url}:`, e.message);
        continue; // Try next service
      }
    }
    
    console.warn('⚠️ All IP services failed, using fallback data');
    return getFallbackLocationData();
  } catch (error) {
    console.error('Error fetching IP address and location:', error);
    return getFallbackLocationData();
  }
};

// Sanitize location data to ensure no undefined values
const sanitizeLocationData = (data) => {
  return {
    ip: data.ip || 'unknown',
    country: data.country || 'Unknown',
    countryCode: data.countryCode || 'XX',
    region: data.region || 'Unknown',
    city: data.city || 'Unknown',
    lat: typeof data.lat === 'number' ? data.lat : 0,
    lon: typeof data.lon === 'number' ? data.lon : 0,
    timezone: data.timezone || 'UTC',
    isp: data.isp || 'Unknown'
  };
};

// Fallback location data when all services fail
const getFallbackLocationData = () => {
  // Try to get some basic info from browser
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const language = navigator.language || 'en';
  
  return {
    ip: 'unknown',
    country: 'Unknown',
    countryCode: 'XX',
    region: 'Unknown',
    city: 'Unknown',
    lat: 0,
    lon: 0,
    timezone: timezone,
    isp: 'Unknown'
  };
};

// Global variable to track ongoing visitor creation to prevent race conditions
let creatingVisitorPromise = null;

// Global state to track if visitor has been initialized
let visitorInfoCache = null;
let isVisitorInitialized = false;

// Get or create a unique visitor based on IP address
const getOrCreateUniqueVisitor = async (ipAddress, locationData) => {
  try {
    const db = getDb();
    if (!db || !ipAddress || ipAddress === 'unknown') {
      // Fallback to session-based ID if no IP
      return getSessionBasedVisitorId();
    }

    // If we're already in the process of creating a visitor for this session, wait for it
    if (creatingVisitorPromise) {
      console.log('🔄 Waiting for ongoing visitor creation...');
      return await creatingVisitorPromise;
    }

    // Create a promise for the visitor creation process
    creatingVisitorPromise = (async () => {
      try {
        // Always check Firestore for the most current data - don't rely on stale session cache
        const visitorsRef = collection(db, 'unique_visitors');
        const q = query(visitorsRef, where('ipAddress', '==', ipAddress));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Visitor exists, get their current data from Firestore
          const visitorDoc = querySnapshot.docs[0];
          const visitorData = visitorDoc.data();
          
          // Always use fresh data from Firestore, not session cache
          const cachedVisitorInfo = {
            visitorId: visitorData.visitorId,
            docId: visitorDoc.id,
            ipAddress: visitorData.ipAddress,
            firstVisit: visitorData.firstVisit,
            totalVisits: visitorData.totalVisits || 1,
            lastVisit: visitorData.lastVisit,
            ...locationData
          };
          
          // Update session cache with fresh data
          sessionStorage.setItem('portfolio_visitor_info', JSON.stringify(cachedVisitorInfo));
          
          console.log('🔍 Found existing visitor for IP:', ipAddress, '- ID:', visitorData.visitorId, '- Current visits:', visitorData.totalVisits);
          return cachedVisitorInfo;
        } else {
          // Create new unique visitor
          const visitorId = `ip_${ipAddress.replace(/\./g, '_')}_${Date.now()}`;
          
          // Ensure all location data is properly sanitized
          const sanitizedLocation = sanitizeLocationData(locationData);
          
          const newVisitor = {
            visitorId,
            ipAddress,
            country: sanitizedLocation.country,
            countryCode: sanitizedLocation.countryCode,
            region: sanitizedLocation.region,
            city: sanitizedLocation.city,
            latitude: sanitizedLocation.lat,
            longitude: sanitizedLocation.lon,
            timezoneFromIP: sanitizedLocation.timezone,
            isp: sanitizedLocation.isp,
            firstVisit: serverTimestamp(),
            lastVisit: serverTimestamp(),
            totalVisits: 1,
            visits: [], // Will store visit details
            userAgents: [navigator.userAgent],
            languages: [navigator.language],
            platforms: [navigator.platform],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };

          const docRef = await addDoc(visitorsRef, newVisitor);
          
          const cachedVisitorInfo = {
            visitorId,
            docId: docRef.id,
            ipAddress,
            firstVisit: new Date().toISOString(),
            totalVisits: 1,
            ...sanitizedLocation
          };
          sessionStorage.setItem('portfolio_visitor_info', JSON.stringify(cachedVisitorInfo));
          
          console.log('✅ New unique visitor created:', visitorId, 'for IP:', ipAddress);
          return cachedVisitorInfo;
        }
      } finally {
        // Clear the promise once completed
        creatingVisitorPromise = null;
      }
    })();

    return await creatingVisitorPromise;

  } catch (error) {
    console.error('❌ Error getting/creating unique visitor:', error);
    creatingVisitorPromise = null; // Clear promise on error
    return getSessionBasedVisitorId();
  }
};

// Fallback visitor ID for cases where IP is not available
const getSessionBasedVisitorId = () => {
  let visitorId = sessionStorage.getItem('portfolio_session_visitor_id');
  if (!visitorId) {
    visitorId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('portfolio_session_visitor_id', visitorId);
  }
  return {
    visitorId,
    ipAddress: 'unknown',
    firstVisit: new Date().toISOString(),
    totalVisits: 1,
    isSessionBased: true
  };
};

// Get session ID (resets on page reload, but persists during session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('portfolio_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('portfolio_session_id', sessionId);
    sessionStorage.setItem('portfolio_session_start', new Date().toISOString());
  }
  return sessionId;
};

/**
 * Track a visit for an existing visitor
 */
const trackVisitForVisitor = async (visitorInfo) => {
  try {
    const db = getDb();
    if (!db || visitorInfo.isSessionBased) return;

    const sessionId = getSessionId();
    const visitData = {
      sessionId,
      visitTime: serverTimestamp(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      referrer: document.referrer || 'direct',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Add visit to the visits collection
    const visitsRef = collection(db, 'visits');
    await addDoc(visitsRef, {
      visitorId: visitorInfo.visitorId,
      ipAddress: visitorInfo.ipAddress,
      ...visitData,
      createdAt: serverTimestamp()
    });

    // ONLY increment if this is a returning visitor (totalVisits > 1)
    if (visitorInfo.docId && visitorInfo.totalVisits > 1) {
      const newTotalVisits = visitorInfo.totalVisits + 1;
      
      const visitorDocRef = doc(db, 'unique_visitors', visitorInfo.docId);
      await updateDoc(visitorDocRef, {
        lastVisit: serverTimestamp(),
        totalVisits: newTotalVisits,
        visits: arrayUnion(visitData),
        updatedAt: serverTimestamp(),
        // Update arrays to include new values if they don't exist
        userAgents: arrayUnion(navigator.userAgent),
        languages: arrayUnion(navigator.language),
        platforms: arrayUnion(navigator.platform)
      });

      // Update cache with new visit count
      visitorInfo.totalVisits = newTotalVisits;
      visitorInfo.lastVisit = new Date().toISOString();
      
      // Update session storage
      const cachedInfo = sessionStorage.getItem('portfolio_visitor_info');
      if (cachedInfo) {
        const parsed = JSON.parse(cachedInfo);
        parsed.totalVisits = newTotalVisits;
        parsed.lastVisit = new Date().toISOString();
        sessionStorage.setItem('portfolio_visitor_info', JSON.stringify(parsed));
      }

      console.log('✅ Return visit tracked for visitor:', visitorInfo.visitorId, '- Total visits:', newTotalVisits);
    } else {
      console.log('✅ First visit tracked for new visitor:', visitorInfo.visitorId);
    }
  } catch (error) {
    console.error('❌ Error tracking visit:', error);
  }
};

/**
 * Get visitor information with caching to avoid repeated Firestore calls
 */
const getVisitorInfo = async () => {
  // Return cached visitor info if already initialized
  if (isVisitorInitialized && visitorInfoCache) {
    return visitorInfoCache;
  }

  // Get location data first
  const locationData = await getIPAddressAndLocation();
  
  // Get or create visitor info
  visitorInfoCache = await getOrCreateUniqueVisitor(locationData.ip, locationData);
  isVisitorInitialized = true;
  
  return visitorInfoCache;
};

/**
 * Initialize visitor tracking (call once per session)
 */
export const initializeVisitorTracking = async () => {
  try {
    // Use a more specific session key that includes a timestamp component
    const sessionInitKey = 'portfolio_visitor_initialized';
    const sessionStartKey = 'portfolio_session_start_time';
    
    // Check if we've already initialized tracking for this browser session
    // But allow re-initialization if it's been more than 30 minutes (new session)
    const lastInit = sessionStorage.getItem(sessionInitKey);
    const sessionStart = sessionStorage.getItem(sessionStartKey);
    const now = Date.now();
    
    if (lastInit && sessionStart) {
      const timeSinceInit = now - parseInt(sessionStart);
      // If less than 30 minutes, skip initialization
      if (timeSinceInit < 30 * 60 * 1000) {
        console.log('⏭️ Visitor tracking already initialized for this session');
        return;
      }
    }

    // Clear any stale session data for fresh initialization
    sessionStorage.removeItem('portfolio_visitor_info');
    sessionStorage.removeItem('portfolio_session_id');
    
    // Reset cache for fresh initialization
    visitorInfoCache = null;
    isVisitorInitialized = false;
    
    console.log('🚀 Initializing visitor tracking...');
    const visitorInfo = await getVisitorInfo();
    
    // Only track visit for RETURNING visitors (those who already exist in DB)
    // New visitors are already counted when created with totalVisits: 1
    if (!visitorInfo.isSessionBased && visitorInfo.totalVisits > 1) {
      await trackVisitForVisitor(visitorInfo);
    }

    // Mark session as initialized with current timestamp
    sessionStorage.setItem(sessionInitKey, now.toString());
    sessionStorage.setItem(sessionStartKey, now.toString());
    
    console.log('✅ Visitor tracking initialized for:', visitorInfo.visitorId, '- Current visits:', visitorInfo.totalVisits);
  } catch (error) {
    console.error('❌ Error initializing visitor tracking:', error);
  }
};

/**
 * Track a page view
 * @param {string} pagePath - The path of the page
 * @param {string} pageTitle - The title of the page
 */
export const trackPageView = async (pagePath, pageTitle = '') => {
  try {
    const db = getDb();
    if (!db) {
      console.error('❌ Firestore db is not initialized');
      return;
    }
    
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    // Ensure all visitor info fields are defined to prevent Firestore errors
    const sanitizedVisitorInfo = {
      visitorId: visitorInfo.visitorId || 'unknown',
      ipAddress: visitorInfo.ipAddress || 'unknown',
      country: visitorInfo.country || 'Unknown',
      countryCode: visitorInfo.countryCode || 'XX',
      region: visitorInfo.region || 'Unknown',
      city: visitorInfo.city || 'Unknown',
      latitude: typeof visitorInfo.lat === 'number' ? visitorInfo.lat : 0,
      longitude: typeof visitorInfo.lon === 'number' ? visitorInfo.lon : 0,
      timezoneFromIP: visitorInfo.timezone || 'UTC',
      isp: visitorInfo.isp || 'Unknown'
    };
    
    const pageViewsRef = collection(db, 'page_views');
    await addDoc(pageViewsRef, {
      pagePath,
      pageTitle: pageTitle || document.title,
      ...sanitizedVisitorInfo,
      sessionId,
      userAgent: navigator.userAgent || 'Unknown',
      language: navigator.language || 'en',
      platform: navigator.platform || 'Unknown',
      screenWidth: window.screen.width || 0,
      screenHeight: window.screen.height || 0,
      viewportWidth: window.innerWidth || 0,
      viewportHeight: window.innerHeight || 0,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      cookieEnabled: navigator.cookieEnabled || false,
      online: navigator.onLine || true,
      createdAt: serverTimestamp(),
    });
    console.log('✅ Page view tracked:', pagePath);
  } catch (error) {
    console.error('❌ Error tracking page view:', error);
    console.error('Error details:', error.message, error.code);
  }
};

/**
 * Track a click event
 * @param {Object} eventData - Click event data
 */
export const trackClick = async (eventData) => {
  try {
    const db = getDb();
    if (!db) {
      console.error('❌ Firestore db is not initialized');
      return;
    }
    
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    const clicksRef = collection(db, 'clicks');
    await addDoc(clicksRef, {
      ...eventData,
      visitorId: visitorInfo.visitorId,
      ipAddress: visitorInfo.ipAddress,
      sessionId,
      pagePath: eventData.pagePath || window.location.pathname,
      userAgent: navigator.userAgent,
      createdAt: serverTimestamp(),
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Click tracked:', eventData.elementType, eventData.elementText);
    }
  } catch (error) {
    console.error('❌ Error tracking click:', error);
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} eventData - Additional event data
 */
export const trackEvent = async (eventName, eventData = {}) => {
  try {
    const db = getDb();
    if (!db) {
      console.error('❌ Firestore db is not initialized');
      return;
    }
    
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    const eventsRef = collection(db, 'events');
    await addDoc(eventsRef, {
      eventName,
      ...eventData,
      visitorId: visitorInfo.visitorId,
      sessionId,
      pagePath: window.location.pathname,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookieEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Track form submission
 * @param {string} formName - Name of the form
 * @param {Object} formData - Form data (sanitized, no sensitive info)
 */
export const trackFormSubmit = async (formName, formData = {}) => {
  try {
    const db = getDb();
    if (!db) {
      console.error('❌ Firestore db is not initialized');
      return;
    }
    
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    const formSubmissionsRef = collection(db, 'form_submissions');
    await addDoc(formSubmissionsRef, {
      formName,
      formData: sanitizeFormData(formData),
      visitorId: visitorInfo.visitorId,
      ipAddress: visitorInfo.ipAddress,
      sessionId,
      pagePath: window.location.pathname,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookieEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error tracking form submission:', error);
  }
};

/**
 * Sanitize form data to remove sensitive information
 */
const sanitizeFormData = (data) => {
  const sensitiveFields = ['password', 'creditCard', 'ssn', 'cvv'];
  const sanitized = { ...data };
  
  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = '[REDACTED]';
    }
  });
  
  return sanitized;
};

/**
 * Track time spent on page
 * @param {string} pagePath - The path of the page
 * @param {number} timeSpent - Time spent in milliseconds
 */
export const trackTimeOnPage = async (pagePath, timeSpent) => {
  try {
    const db = getDb();
    if (!db) {
      console.error('❌ Firestore db is not initialized');
      return;
    }
    
    const visitorInfo = await getVisitorInfo();
    const sessionId = getSessionId();
    
    const timeOnPageRef = collection(db, 'time_on_page');
    await addDoc(timeOnPageRef, {
      pagePath,
      timeSpent,
      visitorId: visitorInfo.visitorId,
      ipAddress: visitorInfo.ipAddress,
      sessionId,
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      cookieEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error tracking time on page:', error);
  }
};

/**
 * Get analytics summary for current visitor
 */
export const getVisitorAnalytics = async () => {
  const visitorInfo = await getVisitorInfo();
  const sessionId = getSessionId();
  
  return {
    visitorId: visitorInfo.visitorId,
    ipAddress: visitorInfo.ipAddress,
    sessionId,
    firstVisit: visitorInfo.firstVisit,
    totalVisits: visitorInfo.totalVisits,
    sessionStart: sessionStorage.getItem('portfolio_session_start'),
    currentPage: window.location.pathname,
    isSessionBased: visitorInfo.isSessionBased || false
  };
};

